import { Component, Host, h, Element, State, Prop, Watch } from '@stencil/core';

@Component({
    tag: 'sew-ws-podcast',
    styleUrl: 'sew-ws-podcast.scss',
    shadow: false,
})
export class SewWsPodcast {
    @Element() el: HTMLSewWsPodcastElement;

    @Prop() headline: string;
    @Prop() thumbHeadline: string;
    @Prop() thumbText: string;
    @Prop() text: string;
    @Prop() audioInfo: string;
    @Prop({ reflect: true }) noGrid = false;
    @Prop({ reflect: true }) noMargin = false;
    @Prop({ reflect: true }) columns = 12;
    @Prop() rewindAriaLabel = 'Rewind';
    @Prop() muteAriaLabel = 'Mute';
    @Prop() closeAriaLabel = 'Close';
    @Prop() forwardAriaLabel = 'Forward';
    @Prop() playAriaLabel = 'Play';

    @State() isPlaying = false;
    @State() showOverlay = false;
    @State() durationInMinutes = '';
    @State() muted = false;

    @Watch('showOverlay')
    watchShowOverlay() {
        const floatingButtons = document.body.querySelector(
            'sew-ws-floating-buttons, sew-ws-floating-buttons-api'
        ) as HTMLElement;

        if (!floatingButtons) {
            return;
        }

        if (this.showOverlay) {
            floatingButtons.style.setProperty('display', 'none');
        } else {
            floatingButtons.style.setProperty('display', 'block');
        }
    }

    private audioElement: HTMLMediaElement;
    private progressElement: HTMLInputElement;
    private volumeElement: HTMLInputElement;
    private currentTimeElement: HTMLSpanElement;

    componentWillLoad(): void {
        this.audioElement = this.el.querySelector('audio');
        this.createAudio();
    }

    private createAudio = async () => {
        await fetch(this.audioElement.src)
            .then(res => {
                if (!res.ok) {
                    throw Error(res.statusText);
                }
                return res.blob();
            })
            .then(blob => {
                this.audioElement = new Audio(URL.createObjectURL(blob));
                this.audioElement.addEventListener('durationchange', this.bootstrap);
            })
            .catch(err => console.error(err));
    };

    private bootstrap = (): void => {
        this.updateProgress();
        this.updateVolume();
        this.durationInMinutes = this.secondsToMinutes(this.audioElement.duration);

        this.audioElement.addEventListener('timeupdate', () => {
            this.updateProgress();
        });
        this.audioElement.addEventListener('ended', () => {
            this.stop();
        });
    };

    private togglePlay = () => {
        this.showOverlay = true;

        if (this.audioElement.paused) {
            this.audioElement.play();
            this.isPlaying = true;
        } else {
            this.stop();
        }
    };

    private stop() {
        this.audioElement.pause();
        this.isPlaying = false;
    }

    private progressChange = () => {
        const targetTime = (this.audioElement.duration * Number(this.progressElement.value)) / 100;
        this.audioElement.currentTime = targetTime;
    };

    private volumeChange = () => {
        const targetVolume = Number(this.volumeElement.value) / 100;
        this.audioElement.volume = targetVolume;
    };

    private updateProgress() {
        const playedPercentage = (this.audioElement.currentTime / this.audioElement.duration) * 100;
        this.progressElement.value = String(playedPercentage);
        this.currentTimeElement.textContent = this.secondsToMinutes(this.audioElement.currentTime);
    }

    private updateVolume() {
        const volumePercentage = this.audioElement.volume * 100;
        this.volumeElement.value = String(volumePercentage);
    }

    private rewind = () => {
        const newTime = Math.max(this.audioElement.currentTime - 15, 0);
        this.audioElement.currentTime = newTime;
    };

    private forward = () => {
        const newTime = Math.min(this.audioElement.currentTime + 15, this.audioElement.duration);
        this.audioElement.currentTime = newTime;
    };

    private mute = () => {
        this.audioElement.muted = !this.audioElement.muted;
        this.muted = this.audioElement.muted;

        if (this.muted) {
            this.volumeElement.value = '0';
        } else {
            this.updateVolume();
        }
    };

    private close = () => {
        this.showOverlay = false;
        this.stop();
    };

    private secondsToMinutes(seconds): string {
        const minutes = String(Math.floor(seconds / 60));
        const secondsLeftover = String(Math.floor(seconds % 60));

        return (
            (minutes.length === 1 ? '0' : '') +
            minutes +
            ':' +
            (secondsLeftover.length === 1 ? '0' : '') +
            secondsLeftover
        );
    }

    render() {
        return (
            <Host>
                <slot name="audio"></slot>
                <div class="teaser main-content-container">
                    <h2 class="headline" innerHTML={this.headline}></h2>
                    <div class="picture">
                        <slot name="picture"></slot>
                        <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 60">
                            <g fill="none" fill-rule="evenodd">
                                <path d="m0 0h100v60h-100z" fill="#ebeced" />
                                <g fill="#34383c">
                                    <rect height="5.5" rx="1.372434" width="2.744868" x="13" y="29.25" />
                                    <rect
                                        height="8.25"
                                        rx="1.16129"
                                        width="2.322581"
                                        x="17.645161"
                                        y="27.875"
                                    />
                                    <rect
                                        height="16.5"
                                        rx="1.16129"
                                        width="2.322581"
                                        x="22.290323"
                                        y="23.75"
                                    />
                                    <rect
                                        height="26.125"
                                        rx="1.16129"
                                        width="2.322581"
                                        x="26.935484"
                                        y="18.25"
                                    />
                                    <rect
                                        height="16.5"
                                        rx="1.16129"
                                        width="2.322581"
                                        x="31.580645"
                                        y="23.75"
                                    />
                                    <rect
                                        height="8.25"
                                        rx="1.16129"
                                        width="2.322581"
                                        x="36.225807"
                                        y="27.875"
                                    />
                                    <rect
                                        height="5.5"
                                        rx="1.16129"
                                        width="2.322581"
                                        x="40.870968"
                                        y="29.25"
                                    />
                                    <rect height="11" rx="1.16129" width="2.322581" x="45.516129" y="26.5" />
                                    <rect height="20.625" rx="1.16129" width="2.322581" x="50.16129" y="21" />
                                </g>
                                <rect
                                    fill="#de0000"
                                    height="31.625"
                                    rx="1.16129"
                                    width="2.322581"
                                    x="54.806452"
                                    y="15.5"
                                />
                                <rect
                                    fill="#de0000"
                                    height="44"
                                    rx="1.16129"
                                    width="2.322581"
                                    x="59.451613"
                                    y="10"
                                />
                                <rect
                                    fill="#de0000"
                                    height="28.875"
                                    rx="1.16129"
                                    width="2.322581"
                                    x="64.096774"
                                    y="16.875"
                                />
                                <rect
                                    fill="#34383c"
                                    height="16.5"
                                    rx="1.16129"
                                    width="2.322581"
                                    x="68.741936"
                                    y="22.75"
                                />
                                <rect
                                    fill="#34383c"
                                    height="6.875"
                                    rx="1.16129"
                                    width="2.322581"
                                    x="73.387097"
                                    y="27.875"
                                />
                                <rect
                                    fill="#34383c"
                                    height="12.375"
                                    rx="1.16129"
                                    width="2.322581"
                                    x="78.032258"
                                    y="25.125"
                                />
                                <rect
                                    fill="#34383c"
                                    height="5.5"
                                    rx="1.16129"
                                    width="2.322581"
                                    x="82.677419"
                                    y="29.25"
                                />
                            </g>
                        </svg>
                    </div>
                    <div class="text">
                        <p>{this.text}</p>
                        <div class="audio">
                            <button
                                class="play-button"
                                onClick={this.togglePlay}
                                aria-label={this.playAriaLabel}
                            >
                                <sew-icon icon={this.isPlaying ? 'pause' : 'play-filled'} size="l"></sew-icon>
                            </button>
                            <div class="description">
                                {this.durationInMinutes} {this.audioInfo}
                            </div>
                        </div>
                    </div>
                </div>
                <div class={{ overlay: true, visible: this.showOverlay }}>
                    <div class="grid">
                        <div class="teaser">
                            <slot name="picture-thumb"></slot>
                            <div class="text">
                                <h4 class="headline" innerHTML={this.thumbHeadline}></h4>
                                <p>{this.thumbText}</p>
                            </div>
                        </div>
                        <div class="controls">
                            <button
                                class="play-button"
                                onClick={this.togglePlay}
                                aria-label={this.playAriaLabel}
                            >
                                <sew-icon icon={this.isPlaying ? 'pause' : 'play-filled'} size="l"></sew-icon>
                            </button>
                            <button onClick={this.rewind} class="rewind" aria-label={this.rewindAriaLabel}>
                                <sew-icon icon="rewind-filled" size="l"></sew-icon>
                            </button>
                            <button onClick={this.forward} class="forward" aria-label={this.forwardAriaLabel}>
                                <sew-icon icon="rewind-filled" size="l"></sew-icon>
                            </button>
                            <sew-slider ws class="progress">
                                <input
                                    type="range"
                                    ref={el => (this.progressElement = el)}
                                    onChange={this.progressChange}
                                />
                            </sew-slider>
                            <div class="time">
                                <span ref={el => (this.currentTimeElement = el)}></span> /{' '}
                                {this.durationInMinutes}
                            </div>
                            <button onClick={this.mute} class="mute" aria-label={this.muteAriaLabel}>
                                <sew-icon icon={this.muted ? 'volume-off' : 'volume-on'}></sew-icon>
                            </button>
                            <sew-slider ws class="volume">
                                <input
                                    type="range"
                                    ref={el => (this.volumeElement = el)}
                                    onInput={this.volumeChange}
                                />
                            </sew-slider>
                        </div>
                        <div class="close">
                            <button onClick={this.close} aria-label={this.closeAriaLabel}>
                                <sew-icon icon="close" />
                            </button>
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
